import { z } from 'zod';

/**
 * Schemas for maintenance plans + their triggers.
 *
 * Two distinct schemas for plan create/update — same pattern as the
 * Equipment / EquipmentType / WorkOrder forms. The create schema
 * carries the target fields (equipmentId / equipmentTypeId /
 * vesselId) which are server-immutable after creation. The update
 * schema only touches the editable subset.
 *
 * Triggers have THREE schemas, one per kind, with conditional
 * required fields. We expose them as a discriminated union so
 * the form component can render kind-specific inputs and zod can
 * report precise errors.
 */

const optionalString = z
  .string()
  .trim()
  .min(1)
  .nullable()
  .or(z.literal(''))
  .transform((v) => (v === '' ? null : v))
  .optional();

const optionalDecimal = z
  .string()
  .trim()
  .regex(/^\d{1,4}(\.\d{1,2})?$/, 'must be a decimal up to 9999.99')
  .nullable()
  .or(z.literal(''))
  .transform((v) => (v === '' ? null : v))
  .optional();

const optionalUuid = z
  .string()
  .uuid()
  .nullable()
  .or(z.literal(''))
  .transform((v) => (v === '' ? null : v))
  .optional();

const WORK_ORDER_TYPES = [
  'preventive',
  'corrective',
  'condition_based',
  'drydock',
  'improvement',
  'inspection',
] as const;
const WORK_ORDER_PRIORITIES = ['low', 'normal', 'high', 'critical'] as const;

export const maintenancePlanCreateSchema = z
  .object({
    title: z.string().trim().min(1).max(500),
    description: optionalString,
    type: z.enum(WORK_ORDER_TYPES),
    priority: z.enum(WORK_ORDER_PRIORITIES).default('normal'),
    estimatedHours: optionalDecimal,
    leadTimeDays: z.coerce.number().int().min(0).max(3650).default(0),
    equipmentId: optionalUuid,
    equipmentTypeId: optionalUuid,
    vesselId: optionalUuid,
  })
  .refine(
    (v) => Boolean(v.equipmentId) !== Boolean(v.equipmentTypeId),
    {
      message: 'Set exactly one of equipmentId or equipmentTypeId',
      path: ['equipmentId'],
    },
  );

export type MaintenancePlanCreateValues = z.infer<
  typeof maintenancePlanCreateSchema
>;

export const maintenancePlanUpdateSchema = z.object({
  title: z.string().trim().min(1).max(500),
  description: optionalString,
  priority: z.enum(WORK_ORDER_PRIORITIES),
  estimatedHours: optionalDecimal,
  leadTimeDays: z.coerce.number().int().min(0).max(3650),
});

export type MaintenancePlanUpdateValues = z.infer<
  typeof maintenancePlanUpdateSchema
>;

// ============================================================
// Trigger schemas — discriminated union by kind
// ============================================================

const baseTrigger = {
  isActive: z.boolean().default(true),
};

export const timeTriggerSchema = z.object({
  ...baseTrigger,
  kind: z.literal('time'),
  intervalDays: z.coerce.number().int().min(1).max(36500),
  anchorDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});

export const counterTriggerSchema = z.object({
  ...baseTrigger,
  kind: z.literal('counter'),
  counterField: z.string().trim().min(1).max(100),
  intervalValue: z
    .string()
    .trim()
    .regex(/^\d{1,9}(\.\d{1,3})?$/, 'must be a positive decimal'),
  seedLastFiredValue: z.coerce.number().optional(),
});

export const cbmTriggerSchema = z.object({
  ...baseTrigger,
  kind: z.literal('cbm'),
  counterField: z.string().trim().min(1).max(100),
  cbmOperator: z.enum(['gt', 'gte', 'lt', 'lte', 'approx_eq']),
  cbmThreshold: z
    .string()
    .trim()
    .regex(/^-?\d{1,9}(\.\d{1,3})?$/, 'must be a decimal'),
});

export const planTriggerCreateSchema = z.discriminatedUnion('kind', [
  timeTriggerSchema,
  counterTriggerSchema,
  cbmTriggerSchema,
]);

export type PlanTriggerCreateValues = z.infer<typeof planTriggerCreateSchema>;
